import React, {FC, useEffect, useMemo} from 'react';

import {sendDesktopAnalyticsPerSession} from '@optimaxdev/analytics/mobile/lib/sendAnalyticsEvent/index';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {GENERAL_EXPERIMENT_INDICATION} from 'constants/analytics/events/general';
import {ApplicationStoreType} from 'desktop/store';
import {useIsOneStepAvailable} from 'hooks/useIsOneStepAvailable/useIsOneStepAvailable';

import {flags} from 'libs/rollout';

import {getDeliveryErrors} from '../../store/selectors/deliveryStep/deliveryStep';
import {isOnlyContactLensesInOrderReview} from '../../store/selectors/orderReviewItems/orderReviewItems';
import {ShippingMethodsListConnected} from '../shippingMethodsList/shippingMethodsList';

import s from './shippingMethods.scss';

type PropsType = {
    error?: string;
    isOnlyLenses?: boolean;
};

/**
 * Shipping methods for checkout delivery step form
 *
 * @constructor
 */
export const ShippingMethods: FC<PropsType> = ({error = '', isOnlyLenses}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const {isOneStepAvailable} = useIsOneStepAvailable();
    const isCustomShippingEnabled = flags.dCheckoutCLCustomShipping.isEnabled();

    useEffect(() => {
        if (isOnlyLenses) {
            sendDesktopAnalyticsPerSession({
                event: GENERAL_EXPERIMENT_INDICATION,
                isInteraction: false,
                label: {
                    expName: 'dCheckoutCLCustomShipping',
                    label: String(isCustomShippingEnabled),
                },
            });
        }
    }, [isOnlyLenses, isCustomShippingEnabled]);

    const noteText = useMemo(() => {
        if (isOneStepAvailable || isFacelift) {
            return (
                <>
                    Production time may require 3 additional days for single vision lenses, and up
                    to 6 days for multifocal lenses.
                </>
            );
        }

        return (
            <>
                Production time may require 3 additional days for
                <br /> single vision lenses, and up to 6 days for multifocal lenses.
            </>
        );
    }, [isOneStepAvailable, isFacelift]);

    if (error)
        return (
            <div className={s.shippingMethodContainer}>
                <div className={s.error} dangerouslySetInnerHTML={{__html: error}} />
            </div>
        );

    return (
        <div className={s.shippingMethodContainer}>
            <ShippingMethodsListConnected />

            {!isOnlyLenses && (
                <p
                    className={classNames(s.bottomNote, {
                        [s.bottomNoteFacelift]: isFacelift,
                        [s.oneStep]: isOneStepAvailable,
                    })}
                >
                    <b>Please Note: </b>
                    {noteText}
                </p>
            )}
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    error: getDeliveryErrors(store),
    isOnlyLenses: isOnlyContactLensesInOrderReview(store),
});

export const ShippingMethodsConnected = connect(mapStateToProps)(ShippingMethods);
